<script setup lang="ts">
  /**
   * Badges & chips — 03-components/07-badges-chips · OSCloud DS 1.1.154
   */
  import { computed } from 'vue'

  import type { BfBadgeVariant } from './BfBadgeTypes'

  const props = withDefaults(defineProps<{ variant?: BfBadgeVariant; subtle?: boolean }>(), {
    variant: 'neutral',
    subtle: false,
  })

  /** The design system's tone vocabulary calls this one `error`; the prop keeps its call-site name. */
  const MODIFIER_BY_VARIANT: Partial<Record<BfBadgeVariant, string>> = { danger: 'error' }

  const toneModifier = computed(() => MODIFIER_BY_VARIANT[props.variant] ?? props.variant)
</script>

<template>
  <span class="badge" :class="[`badge--${toneModifier}`, { 'badge--subtle': subtle }]">
    <slot />
  </span>
</template>

<style scoped lang="scss">
  .badge {
    display: inline-flex;
    align-items: center;
    border: 1px solid transparent;
    border-radius: calc(var(--dk-radius) - 4px); /* rounded-sm -> 4px */
    padding: 2px 10px; /* px-2.5 py-0.5 */
    font-family: var(--bf-font-display);
    font-size: 11px;
    font-weight: var(--bf-fw-medium);
    /* The design system's card gets this by inheritance from its own html{line-height:1.15},
       which it documents as load-bearing. Pinned here because a table cell sets its own
       line-height and the badge would otherwise grow several pixels inside one. */
    line-height: 1.15;
    letter-spacing: 0.025em; /* tracking-wide */
    text-transform: uppercase;
    transition: background-color var(--dk-transition);
    /* Print drops background fills unless the page insists. A badge carries its meaning in the
       fill and writes its label in the fill's contrast colour, so dropping it does not degrade
       to plain text - an Overdue chip prints white-on-white. */
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &--neutral {
      background: hsl(var(--dk-primary));
      color: hsl(var(--dk-primary-foreground));
    }

    /* Neutral's metrics, brand black/white instead of orange. */
    &--monochrome {
      background: var(--bf-black);
      color: var(--bf-white);
    }

    &--success {
      background: var(--bf-green);
      color: var(--bf-white);
    }

    /* Success's metrics on the answered-ticket tint: light orange fill, orange label. */
    &--pending {
      background: var(--bf-orange-ultralight);
      color: var(--bf-orange-dark);
    }

    /* Pale red surface, red text, no hairline. */
    &--warning {
      background: var(--bf-error-bg);
      color: var(--bf-error-accent);
    }

    &--error {
      background: hsl(var(--dk-danger));
      color: hsl(var(--dk-danger-foreground));
    }

    /* Success's metrics on a pale navy tint with a navy label. */
    &--information {
      background: var(--bf-navy-light);
      color: var(--bf-navy);
    }

    /* Navy-mid fill, navy label — information at mid strength. */
    &--information-mid {
      background: var(--bf-navy-mid);
      color: var(--bf-navy);
    }

    /* Navy fill, navy-light label — information at full strength. */
    &--information-dark {
      background: var(--bf-navy);
      color: var(--bf-navy-light);
    }

    &--grey-contrast {
      background: hsl(var(--dk-secondary));
      color: hsl(var(--dk-secondary-foreground));

      &:hover {
        background: hsl(var(--dk-secondary) / 0.8);
      }
    }

    /* Light grey's tint without the hairline. */
    &--light-grey {
      background: var(--bf-blue-light);
      color: var(--bf-dusky-1);
    }

    /* Grey's tint without the hairline. */
    &--grey {
      background: var(--bf-line);
      color: var(--bf-dark-blue);
    }

    &--ghost {
      background: transparent;
      color: hsl(var(--dk-muted-foreground));
    }

    /* --------------------------------------------------------------------
       Bordered family: tinted surface + matching hairline, colours lifted
       from the ticket-status and SSL-certificate badges so the three
       components read as one palette. Badge metrics, not theirs.
       -------------------------------------------------------------------- */

    &--outline-border {
      background: transparent;
      color: hsl(var(--dk-foreground));
      border-color: var(--bf-dusky-3);
    }

    /* Outline's recipe on an opaque white fill - the one to use on a tint, a stripe or a soft
       container, where a transparent outline would take the surface behind it. */
    &--white-border {
      background: var(--bf-white);
      color: hsl(var(--dk-foreground));
      border-color: var(--bf-dusky-3);
    }

    /* Bordered: text at full danger strength, border at 40%. */
    &--warning-border {
      background: transparent;
      color: hsl(var(--dk-danger));
      border-color: hsl(var(--dk-danger) / 0.4);
    }

    /* Warning's pale red surface, red text, warning-border's hairline. */
    &--error-border {
      background: var(--bf-error-bg);
      color: var(--bf-error-accent);
      border-color: hsl(var(--dk-danger) / 0.4);
    }

    &--pending-border {
      background: var(--bf-orange-ultralight);
      color: var(--bf-orange-dark);
      border-color: var(--bf-orange-mid);
    }

    /* Green light surface, green text, softened green hairline. */
    &--success-border {
      background: var(--bf-green-light);
      color: var(--bf-green);
      border-color: var(--bf-green-mid);
    }

    &--grey-border {
      background: var(--bf-line);
      color: var(--bf-dark-blue);
      border-color: var(--bf-dusky-3);
    }

    &--light-grey-border {
      background: var(--bf-blue-light);
      color: var(--bf-dusky-1);
      border-color: var(--bf-line);
    }

    /* Navy light surface, navy text and hairline. */
    &--information-border {
      background: var(--bf-navy-light);
      color: var(--bf-navy);
      border-color: var(--bf-navy-mid);
    }
  }

  /* Emphasis is orthogonal to tone: the tone picks the hue, `--subtle` the pale fill.
     Solid is the default and needs no class. Written flat because SCSS cannot compound
     two modifiers of the same block through `&`. */
  .badge--success.badge--subtle {
    background: var(--bf-green-light);
    color: var(--bf-green);
  }
</style>
